import {UserSession} from "../contract-features-3/domain";
import {Feature} from "../../../integration/representation/feature";
import {Association} from "../../../integration/representation/association";
import {ContractMilestone, Comment} from "../../../integration/representation/contract";
import {FeatureTypeSummary} from "../../../integration/representation/feature-type";
import {ValidationIssue} from "../../../integration/representation/validation-issue";
import {NvdbSubmission} from "../../../integration/representation/nvdb-submission";

export class EventlogState {
    constructor(
        readonly events: Event[] = [],
        readonly fetchingEvents: boolean = true,
        readonly searchFilter: string = '',
        readonly fromDateFilter: Date = null,
        readonly toDateFilter: Date = null,
        readonly sinceLastLoginFilter: boolean = false,
        readonly currentUser: UserSession = null,
        readonly features: Feature[] = [],
        readonly fetchingFeatures: boolean = true,
        readonly associations: Association[] = [],
        readonly fetchingAssociations: boolean = true,
        readonly milestones: ContractMilestone[] = [],
        readonly fetchingContract: boolean = true,
        readonly featureTypeSummary: FeatureTypeSummary[] = [],
        readonly fetchingFeatureTypeSummary: boolean = true,
        readonly comments: Comment[] = [],
        readonly featureTypeIssues: { [key: number]: ValidationIssue[] } = {},
        readonly featureTypeComments: { [key: string]: Comment[] } = {},
        readonly fkbFeatures: Feature[] = [],
        readonly fetchingFkbFeatures: boolean = true,
        readonly nvdbSubmissions: NvdbSubmission[] = [],
        readonly fetchingNvdbSubmissions: boolean = true
    ) { }

    private create(overrides: Partial<EventlogState>) {
        const params = {...this, ...overrides};
        const state = new EventlogState(
            params.events,
            params.fetchingEvents,
            params.searchFilter,
            params.fromDateFilter,
            params.toDateFilter,
            params.sinceLastLoginFilter,
            params.currentUser,
            params.features,
            params.fetchingFeatures,
            params.associations,
            params.fetchingAssociations,
            params.milestones,
            params.fetchingContract,
            params.featureTypeSummary,
            params.fetchingFeatureTypeSummary,
            params.comments,
            params.featureTypeIssues,
            params.featureTypeComments,
            params.fkbFeatures,
            params.fetchingFkbFeatures,
            params.nvdbSubmissions,
            params.fetchingNvdbSubmissions
        );
        return state;
    }

    public withEvents(events: Event[]) {
        return this.create({events});
    }

    public withFetchingEvents(fetchingEvents: boolean){
        return this.create({fetchingEvents});
    }

    public withSearchFilter(searchFilter: string) {
        return this.create({searchFilter});
    }

    public withFromDateFilter(fromDateFilter: Date) {
        return this.create({fromDateFilter});
    }

    public withToDateFilter(toDateFilter: Date) {
        return this.create({toDateFilter});
    }

    public withSinceLastLoginFilter(sinceLastLoginFilter: boolean) {
        return this.create({sinceLastLoginFilter});
    }

    public withCurrentUser(currentUser: UserSession) {
        return this.create({currentUser});
    }

    public withFeatures(features: Feature[]) {
        return this.create({features});
    }

    public withFetchingFeatures(fetchingFeatures: boolean) {
        return this.create({fetchingFeatures});
    }

    public withAssociations(associations: Association[]) {
        return this.create({associations});
    }

    public withFetchingAssociations(fetchingAssociations: boolean) {
        return this.create({fetchingAssociations});
    }

    public withMilestones(milestones: ContractMilestone[]) {
        return this.create({milestones});
    }

    public withFetchingContract(fetchingContract: boolean) {
        return this.create({fetchingContract});
    }

    public withFeatureTypeSummary(featureTypeSummary: FeatureTypeSummary[]) {
        return this.create({featureTypeSummary});
    }

    public withFetchingFeatureTypeSummary(fetchingFeatureTypeSummary: boolean) {
        return this.create({fetchingFeatureTypeSummary});
    }

    public withComments(comments: Comment[]) {
        return this.create({comments});
    }

    public withFeatureTypeIssues(featureTypeIssues: { [key: number]: ValidationIssue[] }) {
        return this.create({featureTypeIssues});
    }

    public withFeatureTypeComments(featureTypeComments: { [key: string]: Comment[] }) {
        return this.create({featureTypeComments})
    }

    public withFkbFeatures(fkbFeatures: Feature[]) {
        return this.create({fkbFeatures});
    }

    public withFetchingFkbFeatures(fetchingFkbFeatures: boolean) {
        return this.create({fetchingFkbFeatures});
    }

    public withNvdbSubmissions(nvdbSubmissions: NvdbSubmission[]) {
        return this.create({nvdbSubmissions});
    }

    public withFetchingNvdbSubmissions(fetchingNvdbSubmissions: boolean){
        return this.create({fetchingNvdbSubmissions});
    }

}

export type Event = {
    id: string,
    occurredInstant: Date,
    userId: string,
    eventType: string,
    objectType: string,
    object: string,
    data: string
}